import { BUILD_LIMITS, GAME_QUOTAS } from '@mogzo/shared';
import { Code, DocLink, DocTable, Lead } from '../parts';

const MB = 1024 * 1024;

const COMMANDS: [string, string][] = [
  ['mogzo login', 'Sign this computer in (approve a code on dev.mogzo.com).'],
  ['mogzo logout', 'Sign this computer out.'],
  ['mogzo whoami', 'Who you’re signed in as, and your projects.'],
  ['mogzo create <name>', 'Make a project on MOGZO and a game folder for it.'],
  ['mogzo dev', 'Play the game locally, inside a stand-in for mogzo.com.'],
  ['mogzo validate', 'Check mogzo.json and the files, without uploading.'],
  ['mogzo build', 'Run the game’s build command and package .mogzo/build.zip.'],
  ['mogzo upload', 'Build and upload; it plays in your test community.'],
  ['mogzo submit', 'Send the newest build, its source and your store listing for review.'],
  ['mogzo leaderboards [list]', 'The game’s test leaderboards; create, edit or delete one.'],
  ['mogzo achievements [list]', 'The game’s test achievements; icons lists MOGZO’s icons.'],
  [
    'mogzo modes [list]',
    'The game’s multiplayer modes on test (--live for live’s); create, edit or delete one.',
  ],
  [
    'mogzo events [list]',
    'The game’s live events and rules on test (--live for live’s); create, edit or delete an event; rules create or delete a rule.',
  ],
  ['mogzo listing pull', 'Write the store listing’s draft into listing/, as files.'],
  ['mogzo listing push', 'Send listing/ back as the draft; it shows what changes and asks first.'],
  ['mogzo listing check', 'What the files still need before the listing can be submitted.'],
  ['mogzo screenshots', 'Play the game in Chrome or Edge, without a window, and save screenshots.'],
  ['mogzo sdk update', 'Give this game the newest SDK (its mogzo-sdk.js) and AGENTS.md.'],
  ['mogzo update', 'Install the newest MOGZO CLI.'],
];

const OPTIONS: [string, string, string][] = [
  ['create', '--template <t>', 'vanilla (the default) or phaser'],
  ['create', '--address <a>', 'The game’s address on MOGZO (default: from the name)'],
  ['create', '--folder <dir>', 'Where to make it (default: the address)'],
  ['dev', '--port <n>', 'Page port (default 4321; the game uses the next one)'],
  ['dev', '--player <name>', 'The test player’s name'],
  ['dev', '--guest', 'Play as a guest instead'],
  ['upload', '--notes <text>', 'A note for this build, shown in the dashboard'],
  ['submit', '--build <n>', 'Send build #n instead of the newest'],
  ['submit', '--notes <text>', 'A note for the reviewer'],
  ['submit', '--yes', 'Send even if files changed since that build was uploaded'],
  ['submit', '--listing', 'Send only your saved store listing (a live game; no new build)'],
  ['leaderboards', '--name, --highest-first, --lowest-first', 'Its name, and which score is best'],
  ['leaderboards', '--time, --number', 'Show scores as a time (send milliseconds) or a number'],
  ['leaderboards', '--reset <r>', 'never, daily, weekly or monthly (00:00 UTC)'],
  ['leaderboards', '--min <n>, --max <n>', 'Lowest and highest allowed score, or none'],
  ['achievements', '--name, --description, --points', 'Points 0 to 100 (default 10)'],
  ['achievements', '--steps <n>', 'Unlocks when progress reaches this'],
  ['achievements', '--hidden, --shown', 'Keep it secret until earned, or not'],
  ['achievements', '--icon <name> --color <c>', 'A MOGZO icon (default trophy, yellow)'],
  ['achievements', '--image <file>, --no-icon', 'Your own square picture, or no icon'],
  ['modes', '--name, --kind <k>', 'Its name, and turns, bases or realtime (default turns)'],
  [
    'modes',
    '--players <n-m>, --teams <0|2>',
    'How many play, like 2 or 2-6, and two teams or none',
  ],
  ['modes', '--meet <how>', 'quick, invite or both (default both)'],
  [
    'modes',
    '--skill, --no-skill, --widen <time>',
    'Quick match by skill (default on, widening after 15s)',
  ],
  [
    'modes',
    '--turn-time <t>, --missed <m>',
    'Time for a turn, 30s to 7d; skip or forfeit when it runs out',
  ],
  [
    'modes',
    '--host, --late-join, --rate, --size, --room',
    'Realtime: who hosts, joining late, message limits, room length',
  ],
  ['modes', '--leaderboard <id>', 'Count wins on a leaderboard, or none'],
  ['listing push', '--yes', 'Push without asking'],
  ['listing push', '--replace', 'Overwrite fields also changed on dev.mogzo.com since your pull'],
  ['listing pull', '--yes', 'Replace files with changes you haven’t pushed'],
  ['screenshots', '--at <times>', 'When to take them, from the start (default 3s,10s,20s)'],
  ['screenshots', '--params <json>', 'Launch params for the game, like {"demo":"1"}'],
  ['screenshots', '--device <d>', 'computer or phone (default: the listing’s devices)'],
  ['screenshots', '--cover', 'A 1920 × 1080 cover instead'],
  ['screenshots', '--browser <path>', 'The Chrome or Edge to use, if it isn’t found'],
  ['game setup, listing', '--json', 'Answer in JSON, for AI coding tools and scripts'],
  ['game setup', '--yes', 'Delete without asking'],
  ['game setup, listing', '--project <a>', 'Another of your projects (default: id in mogzo.json)'],
  ['every command', '--api <url>', 'The MOGZO API (default https://api.mogzo.com)'],
];

export function CliCommands() {
  return (
    <>
      <h1>CLI commands</h1>
      <Lead>
        Every command of the MOGZO CLI. <Code>mogzo --help</Code> shows the same in your terminal.
      </Lead>

      <h2 id="commands">Commands</h2>
      <DocTable
        head={['Command', 'What it does']}
        rows={COMMANDS.map(([command, what]) => [<Code key="c">{command}</Code>, what])}
      />

      <h2 id="options">Options</h2>
      <DocTable
        head={['For', 'Option', 'What it does']}
        rows={OPTIONS.map(([command, option, what]) => [
          command,
          <Code key="c">{option}</Code>,
          what,
        ])}
      />
    </>
  );
}

export function Limits() {
  return (
    <>
      <h1>Limits</h1>
      <Lead>What a build can hold, and how much each player can use in each game.</Lead>

      <h2 id="builds">Builds</h2>
      <ul>
        <li>
          {BUILD_LIMITS.zipBytes / MB} MB zipped, {BUILD_LIMITS.unpackedBytes / MB} MB unzipped,{' '}
          {BUILD_LIMITS.files.toLocaleString('en')} files per build.
        </li>
        <li>Uploads leave out dot-files and folders (.env, .git) and node_modules.</li>
        <li>
          Each upload is a new build with its own address. The newest 5 ready builds stay playable;
          older ones’ files are removed to save space (the dashboard still lists them).
        </li>
        <li>
          Games run on mogzogames.online, in a frame that can’t open pop-ups or leave the page.
        </li>
      </ul>

      <h2 id="services">Game Services</h2>
      <p>Per player, in each game:</p>
      <DocTable
        head={['What', 'Limit', 'How MOGZO sends it']}
        rows={[
          [
            'All calls',
            `${GAME_QUOTAS.callsPerMinute} a minute`,
            'Calls answered on the page don’t count.',
          ],
          [
            <DocLink key="l" page="cloud-saves">
              Saves
            </DocLink>,
            `${GAME_QUOTAS.savesPerPlayer}, ${GAME_QUOTAS.saveBytes / 1024} KB each`,
            'First write each session at once; then at most once a minute, and on pause, hide or close.',
          ],
          [
            <DocLink key="l" page="leaderboards">
              Scores
            </DocLink>,
            `${GAME_QUOTAS.scoresPerMinute} a minute per leaderboard`,
            'At once. Send one at the end of a run.',
          ],
          [
            <DocLink key="l" page="achievements">
              Achievements
            </DocLink>,
            `${GAME_QUOTAS.achievements} per game`,
            'Unlocks at once; repeat unlocks free. Progress at once when it unlocks, otherwise once a minute.',
          ],
          [
            <DocLink key="l" page="game-data">
              Game data
            </DocLink>,
            `${GAME_QUOTAS.documentBytes / 1024} KB a document, ${GAME_QUOTAS.documentsPerPlayer} per player`,
            'At once.',
          ],
          [
            <DocLink key="l" page="remote-config">
              Remote config
            </DocLink>,
            `${GAME_QUOTAS.configBytes / 1024} KB`,
            'Read once, then reused for 5 minutes, or until an event turns on or off.',
          ],
          [
            <DocLink key="l" page="live-events">
              Live events
            </DocLink>,
            `${GAME_QUOTAS.events} events and ${GAME_QUOTAS.rules} rules per game`,
            'Read once, then reused for 5 minutes, or until an event turns on or off.',
          ],
          [
            <DocLink key="l" page="multiplayer">
              Multiplayer
            </DocLink>,
            `${GAME_QUOTAS.modes} modes per game`,
            'While a match waits, MOGZO’s panel checks on it every 2 seconds.',
          ],
          [
            'Storage',
            `${GAME_QUOTAS.storageBytes / MB} MB per game`,
            'Saves and game data together.',
          ],
        ]}
      />

      <h2 id="design">Designing for them</h2>
      <p>
        Aim for about 4 calls a minute per player in a typical session; that’s what MOGZO is sized
        for. Save after something happens rather than every frame, send scores at the end of a run,
        and read config once at the start.
      </p>
    </>
  );
}

const PROBLEMS: [string, string][] = [
  [
    '“You’re not signed in”',
    'Run mogzo login. Sign-ins end after 90 days unused, after a password change, or when signed out under CLI sessions.',
  ],
  [
    '“mogzo.json says "id" …, but you have no project with that address”',
    'The id must be the address of one of your projects. Fix the id, or create the project in the dashboard first.',
  ],
  [
    '“The entry file … isn’t in the game folder”',
    'entry must name a file that exists, relative to mogzo.json (or to build.output).',
  ],
  [
    'The test page says “The game is still loading”',
    'The game didn’t finish loading within 15 seconds. Check it in mogzo dev; the browser console shows errors from the game.',
  ],
  [
    'The game never greets the player',
    'It isn’t calling mogzo.init(). Load mogzo-sdk.js before your game’s script, and call init() once at start.',
  ],
  [
    '“command not found: mogzo” after installing',
    'Close and reopen the terminal. If it persists, check that npm’s global folder (npm prefix -g) is on your PATH.',
  ],
  [
    'A call fails with too_many_calls',
    'The game made more than 120 Game Services calls in a minute for one player. Save and send scores less often; see Limits.',
  ],
];

export function Troubleshooting() {
  return (
    <>
      <h1>When something goes wrong</h1>
      <Lead>Common messages, and what to do about them.</Lead>
      <h2 id="problems">Problems and fixes</h2>
      <dl>
        {PROBLEMS.map(([problem, fix]) => (
          <div key={problem}>
            <dt>{problem}</dt>
            <dd>{fix}</dd>
          </div>
        ))}
      </dl>
    </>
  );
}
